import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, fromEvent, interval, map, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  WorkflowDataService,
  WorkflowErrorsService,
  WorkflowOfflineService,
  WorkflowService,
  WorkflowStorageOwnerService,
  type QueuedWorkflowCompletion,
  type QueuedWorkflowSubmission,
  type WorkflowStorageOwner,
} from '@core/services/workflow';
import { PhotoQueueService } from '@core/services/workflow/photo-queue.service';
import { RequestService } from '@core/services/request/request.service';
import {
  classifyWorkflowError,
  classifyWorkflowResponse,
  getWorkflowRetryDelayMs,
} from '@core/services/workflow/workflow-sync-result';
import type { WorkflowSyncClassification } from '@core/services/workflow/workflow-offline.types';

/**
 * Процессор офлайн-очереди регламентов.
 *
 * - задачи читаются/отправляются/удаляются только для владельца, зафиксированного на проход;
 * - сабмиты разбиты на партиции по экземпляру формы: внутри партиции строго по порядку,
 *   партиции независимы — «ядовитая» задача не блокирует другие формы и заявки;
 * - задача удаляется только при success; validation-failed/terminal остаются с ошибкой,
 *   retryable — ждут повтора с увеличивающейся задержкой;
 * - завершение регламентов заявки отправляется, только когда по заявке нет
 *   неотправленных сабмитов и задач фото.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineQueueService {
  private readonly workflowOfflineService = inject(WorkflowOfflineService);
  private readonly workflowService = inject(WorkflowService);
  private readonly ownerService = inject(WorkflowStorageOwnerService);
  private readonly photoQueueService = inject(PhotoQueueService);
  private readonly errorsService = inject(WorkflowErrorsService);
  private readonly dataService = inject(WorkflowDataService);
  private readonly requestService = inject(RequestService);
  private readonly destroyRef = inject(DestroyRef);

  private processingPromise: Promise<void> | null = null;
  private rerunRequested = false;
  private rerunIgnoreBackoff = false;

  /**
   * Запустить обработку: каждые 30 секунд, при восстановлении связи (без ожидания backoff)
   * и при добавлении задачи в очередь
   */
  startQueueProcessor(): void {
    merge(
      interval(30000).pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
      this.workflowOfflineService.queueChanged$.pipe(map(() => false)),
    )
      .pipe(
        filter(() => navigator.onLine),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(ignoreBackoff => {
        void this.processQueue(ignoreBackoff).catch(error =>
          console.error('Offline queue processing failed:', error),
        );
      });
  }

  /**
   * Один проход по очереди. Параллельные вызовы не запускают второй проход,
   * а планируют повтор после текущего.
   */
  processQueue(ignoreBackoff = false): Promise<void> {
    if (this.processingPromise) {
      this.rerunRequested = true;
      this.rerunIgnoreBackoff = this.rerunIgnoreBackoff || ignoreBackoff;

      return this.processingPromise;
    }

    const processingPromise = this.runPass(ignoreBackoff).finally(() => {
      this.processingPromise = null;

      if (this.rerunRequested) {
        const rerunIgnoreBackoff = this.rerunIgnoreBackoff;

        this.rerunRequested = false;
        this.rerunIgnoreBackoff = false;
        void this.processQueue(rerunIgnoreBackoff);
      }
    });

    this.processingPromise = processingPromise;

    return processingPromise;
  }

  /**
   * Ручной запуск (восстановление связи): задержки повторов не ждём
   */
  async manualSync(): Promise<void> {
    if (!navigator.onLine) {
      console.warn('Cannot sync: device is offline');

      return;
    }

    await this.processQueue(true);
  }

  async getPendingCount(): Promise<number> {
    return this.workflowOfflineService.getQueueCount();
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  private async runPass(ignoreBackoff: boolean): Promise<void> {
    // Владелец фиксируется на весь проход: задачи читаются, отправляются и удаляются
    // только для него, а при смене сессии обработка прекращается
    const owner = this.ownerService.getOwner();

    if (!owner || !navigator.onLine) {
      return;
    }

    const submissions =
      await this.workflowOfflineService.getSubmissionQueue(owner);
    const partitions = new Map<string, QueuedWorkflowSubmission[]>();

    for (const submission of submissions) {
      partitions.set(submission.instanceKey, [
        ...(partitions.get(submission.instanceKey) ?? []),
        submission,
      ]);
    }

    await Promise.all(
      [...partitions.values()].map(partition =>
        this.processPartition(partition, owner, ignoreBackoff),
      ),
    );

    await this.processCompletions(owner, ignoreBackoff);
  }

  private async processPartition(
    partition: QueuedWorkflowSubmission[],
    owner: WorkflowStorageOwner,
    ignoreBackoff: boolean,
  ): Promise<void> {
    const failedBefore: QueuedWorkflowSubmission[] = [];

    for (const task of partition) {
      if (!this.isCurrentOwner(owner)) {
        return;
      }

      // Упавшая задача ждёт действий пользователя; более новые сохранения этой формы
      // идут дальше и, при успехе, заменяют её
      if (task.status === 'failed') {
        failedBefore.push(task);
        continue;
      }

      if (
        !ignoreBackoff &&
        task.status === 'retry-wait' &&
        task.nextAttemptAt > Date.now()
      ) {
        // Порядок внутри экземпляра формы сохраняется: более новые задачи ждут эту
        return;
      }

      const classification = await this.sendSubmission(task);

      if (classification.kind === 'success') {
        await this.workflowOfflineService.removeSubmissionFromQueue(
          task.id,
          owner,
        );

        for (const superseded of failedBefore) {
          await this.workflowOfflineService.removeSubmissionFromQueue(
            superseded.id,
            owner,
          );
        }

        failedBefore.length = 0;
        await this.onSubmissionSynced(task, owner);
        continue;
      }

      if (classification.kind === 'retryable') {
        const attempts = task.attempts + 1;

        await this.workflowOfflineService.saveSubmission(
          {
            ...task,
            attempts,
            status: 'retry-wait',
            nextAttemptAt: Date.now() + getWorkflowRetryDelayMs(attempts),
            lastError: classification.error,
          },
          owner,
        );

        return;
      }

      // validation-failed / terminal: задача остаётся с ошибкой и показывается пользователю
      await this.workflowOfflineService.saveSubmission(
        {
          ...task,
          attempts: task.attempts + 1,
          status: 'failed',
          lastError: classification.error,
        },
        owner,
      );
      failedBefore.push(task);

      if (classification.kind === 'validation-failed') {
        await this.onSubmissionValidationFailed(task, classification, owner);
      }
    }
  }

  private async sendSubmission(
    task: QueuedWorkflowSubmission,
  ): Promise<WorkflowSyncClassification> {
    try {
      const response = await firstValueFrom(
        this.workflowService.syncWorkflow(
          task.requestNumber,
          task.workflowId,
          task.nodeId,
          task.formId,
          task.body,
          task.operationId,
        ),
      );

      return classifyWorkflowResponse(response);
    } catch (error) {
      return classifyWorkflowError(error);
    }
  }

  private async processCompletions(
    owner: WorkflowStorageOwner,
    ignoreBackoff: boolean,
  ): Promise<void> {
    const completions =
      await this.workflowOfflineService.getCompletionQueue(owner);

    if (completions.length === 0) {
      return;
    }

    const [submissions, photoTasks] = await Promise.all([
      this.workflowOfflineService.getSubmissionQueue(owner),
      this.photoQueueService.getAll(),
    ]);

    for (const completion of completions) {
      if (!this.isCurrentOwner(owner)) {
        return;
      }

      const hasPendingWork =
        submissions.some(
          task => task.requestNumber === completion.requestNumber,
        ) ||
        photoTasks.some(
          task =>
            task.target.requestNumber === completion.requestNumber &&
            task.state !== 'succeeded',
        );

      if (
        hasPendingWork ||
        completion.status === 'failed' ||
        (!ignoreBackoff &&
          completion.status === 'retry-wait' &&
          completion.nextAttemptAt > Date.now())
      ) {
        continue;
      }

      await this.sendCompletion(completion, owner);
    }
  }

  private async sendCompletion(
    completion: QueuedWorkflowCompletion,
    owner: WorkflowStorageOwner,
  ): Promise<void> {
    let classification: WorkflowSyncClassification;

    try {
      const response = await firstValueFrom(
        this.workflowService.completeRequestWorkflows(
          completion.requestNumber,
          completion.operationId,
        ),
      );

      classification = classifyWorkflowResponse(response);
    } catch (error) {
      classification = classifyWorkflowError(error);

      // Регламенты уже завершены (например, с другого устройства) — цель достигнута
      if (classification.error?.failureReason === 'alreadyCompleted') {
        classification = { kind: 'success' };
      }
    }

    if (classification.kind === 'success') {
      await this.workflowOfflineService.removeCompletionFromQueue(
        completion.requestNumber,
        owner,
      );
      this.refreshIfCurrentRequest(completion.requestNumber, owner);

      return;
    }

    const attempts = completion.attempts + 1;

    await this.workflowOfflineService.saveCompletion(
      {
        ...completion,
        attempts,
        status: classification.kind === 'retryable' ? 'retry-wait' : 'failed',
        nextAttemptAt:
          classification.kind === 'retryable'
            ? Date.now() + getWorkflowRetryDelayMs(attempts)
            : completion.nextAttemptAt,
        lastError: classification.error,
      },
      owner,
    );
  }

  /**
   * Побочные эффекты выполняются в хранилище владельца задачи, зафиксированного до запроса:
   * если сессия сменилась во время запроса, данные нового пользователя не затрагиваются
   */
  private async onSubmissionSynced(
    task: QueuedWorkflowSubmission,
    owner: WorkflowStorageOwner,
  ): Promise<void> {
    await this.errorsService.removeFormErrors(
      task.requestNumber,
      task.workflowId,
      task.nodeId,
      task.formId,
      owner,
    );
    await this.dataService.removeFormDraft(
      task.requestNumber,
      task.workflowId,
      task.nodeId,
      task.formId,
      owner,
    );
    this.refreshIfCurrentRequest(task.requestNumber, owner);
  }

  private async onSubmissionValidationFailed(
    task: QueuedWorkflowSubmission,
    classification: WorkflowSyncClassification,
    owner: WorkflowStorageOwner,
  ): Promise<void> {
    const validationErrors = classification.error?.validationErrors ?? [];

    if (validationErrors.length > 0) {
      try {
        await this.errorsService.saveFormErrors(
          task.requestNumber,
          task.workflowId,
          task.nodeId,
          task.formId,
          validationErrors,
          owner,
        );
      } catch (error) {
        console.error('Failed to save queued form validation errors:', error);
      }
    }

    this.refreshIfCurrentRequest(task.requestNumber, owner);
  }

  /**
   * Если заявка открыта — перечитать данные регламентов с сервера (серверная валидность)
   */
  private refreshIfCurrentRequest(
    requestNumber: string,
    owner: WorkflowStorageOwner,
  ): void {
    // Ответ задачи прежнего пользователя не перезагружает экран нового
    if (
      this.isCurrentOwner(owner) &&
      this.requestService.$request()?.requestNumber === requestNumber
    ) {
      this.workflowService.refreshEntities();
    }
  }

  private isCurrentOwner(owner: WorkflowStorageOwner): boolean {
    const current = this.ownerService.getOwner();

    return (
      current?.environment === owner.environment &&
      current?.userId === owner.userId
    );
  }
}
